import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  TextField,
  Divider,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Grid,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';

// Sample components to preview theme
function SampleComponents({ themeData }) {
  // Create a theme from the provided theme data
  const previewTheme = createTheme(themeData);
  
  return (
    <ThemeProvider theme={previewTheme}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Typography</Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="h1" gutterBottom>Heading 1</Typography>
        <Typography variant="h2" gutterBottom>Heading 2</Typography>
        <Typography variant="h3" gutterBottom>Heading 3</Typography>
        <Typography variant="h4" gutterBottom>Heading 4</Typography>
        <Typography variant="h5" gutterBottom>Heading 5</Typography>
        <Typography variant="h6" gutterBottom>Heading 6</Typography>
        <Typography variant="subtitle1" gutterBottom>Subtitle 1</Typography>
        <Typography variant="subtitle2" gutterBottom>Subtitle 2</Typography>
        <Typography variant="body1" gutterBottom>
          Body 1. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
        </Typography>
        <Typography variant="body2" gutterBottom>
          Body 2. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
        </Typography>
        <Typography variant="button" display="block" gutterBottom>Button Text</Typography>
        <Typography variant="caption" display="block" gutterBottom>Caption Text</Typography>
        <Typography variant="overline" display="block" gutterBottom>Overline Text</Typography>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Buttons</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Button variant="contained" color="primary">Primary</Button>
          <Button variant="contained" color="secondary">Secondary</Button>
          <Button variant="contained" color="error">Error</Button>
          <Button variant="contained" color="warning">Warning</Button>
          <Button variant="contained" color="info">Info</Button>
          <Button variant="contained" color="success">Success</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
          <Button variant="outlined" color="primary">Primary</Button>
          <Button variant="outlined" color="secondary">Secondary</Button>
          <Button variant="outlined" color="error">Error</Button>
          <Button variant="outlined" color="warning">Warning</Button>
          <Button variant="outlined" color="info">Info</Button>
          <Button variant="outlined" color="success">Success</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Button variant="text" color="primary">Primary</Button>
          <Button variant="text" color="secondary">Secondary</Button>
          <Button variant="text" color="error">Error</Button>
          <Button variant="text" color="warning">Warning</Button>
          <Button variant="text" color="info">Info</Button>
          <Button variant="text" color="success">Success</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}>With Icon</Button>
          <Button variant="contained" color="secondary" endIcon={<SaveIcon />}>With Icon</Button>
          <IconButton color="primary" aria-label="add"><AddIcon /></IconButton>
          <IconButton color="secondary" aria-label="edit"><EditIcon /></IconButton>
          <IconButton color="error" aria-label="delete"><DeleteIcon /></IconButton>
        </Box>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Cards</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div" gutterBottom>
                  Card Title
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This is a sample card with some content. The styling of this card is determined by the theme configuration.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small">Learn More</Button>
                <Button size="small" color="primary">Action</Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div" gutterBottom>
                  Another Card
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cards can be used to display content in a contained manner. They can contain actions, text, and more.
                </Typography>
              </CardContent>
              <CardActions>
                <Button size="small" variant="contained" color="primary">Primary</Button>
                <Button size="small" variant="outlined" color="secondary">Secondary</Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Form Elements</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 500 }}>
          <TextField label="Standard" variant="outlined" />
          <TextField label="With Helper Text" variant="outlined" helperText="Some helper text" />
          <TextField label="With Error" variant="outlined" error helperText="This field has an error" />
          <TextField label="Disabled" variant="outlined" disabled />
          <FormControlLabel control={<Switch defaultChecked />} label="Switch" />
          <FormControlLabel control={<Switch />} label="Unchecked Switch" />
          <FormControlLabel control={<Switch disabled />} label="Disabled Switch" />
        </Box>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Alerts & Chips</Typography>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity="success">This is a success alert — check it out!</Alert>
          <Alert severity="info">This is an info alert — check it out!</Alert>
          <Alert severity="warning">This is a warning alert — check it out!</Alert>
          <Alert severity="error">This is an error alert — check it out!</Alert>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          <Chip label="Primary" color="primary" />
          <Chip label="Secondary" color="secondary" />
          <Chip label="Success" color="success" />
          <Chip label="Error" color="error" />
          <Chip label="Warning" color="warning" />
          <Chip label="Info" color="info" />
          <Chip label="Default" />
          <Chip label="Clickable" onClick={() => {}} />
          <Chip label="Deletable" onDelete={() => {}} />
          <Chip icon={<AddIcon />} label="With Icon" />
        </Box>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Tabs</Typography>
        <Divider sx={{ mb: 2 }} />
        <Tabs value={0} aria-label="basic tabs example">
          <Tab label="Tab One" />
          <Tab label="Tab Two" />
          <Tab label="Tab Three" />
        </Tabs>
        <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderTop: 0 }}>
          <Typography>Tab content would appear here</Typography>
        </Box>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Paper & Surfaces</Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="body1">Elevation 1</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={3} sx={{ p: 2 }}>
              <Typography variant="body1">Elevation 3</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={6} sx={{ p: 2 }}>
              <Typography variant="body1">Elevation 6</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>Custom Properties</Typography>
        <Divider sx={{ mb: 2 }} />
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1">App Name: <strong>{themeData.custom?.appName || 'Supply Chain Management'}</strong></Typography>
          <Typography variant="subtitle1">Footer Text: <strong>{themeData.custom?.footerText || '© 2023 Supply Chain Inc. All rights reserved.'}</strong></Typography>
          <Typography variant="subtitle1">Navbar Position: <strong>{themeData.custom?.navbarPosition || 'left'}</strong></Typography>
          <Typography variant="subtitle1">Dashboard Layout: <strong>{themeData.custom?.dashboardLayout || 'standard'}</strong></Typography>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}

export default SampleComponents; 